import { v4 } from 'uuid';
import _ from 'lodash';

import { nullOperators, operators } from '../constants/filter';

const emptyTypeExtractor = () => undefined;

const segmentFilter = (condition) => condition.field === 'segments';
const emptyConditionFilter = ({ value, operator }) =>
  nullOperators.includes(operator) || (value != null && (!Array.isArray(value) || !!value.length));

const toNullCondition = ({ field, operator }) => ({
  field,
  operator: operator === operators.is_null ? '=' : '!=',
  value: null,
});

const fromNullCondition = ({ field, operator }, typeExtractor = emptyTypeExtractor) => ({
  id: v4(),
  field,
  type: typeExtractor(field),
  operator: operator === '=' ? operators.is_null : operators.not_null,
  value: null,
});

const checkNullCondition = ({ operator, value }) =>
  (value === null || value === undefined) && (operator === '=' || operator === '!=');

export const fromCondition = ({ field, operator, value }, typeExtractor = emptyTypeExtractor) =>
  checkNullCondition({ operator, value })
    ? fromNullCondition({ field, operator }, typeExtractor)
    : {
        id: v4(),
        field,
        type: typeExtractor(field),
        operator,
        value,
      };

export const fromSegmetCondition = (condition) => ({
  id: v4(),
  field: 'segments',
  type: 'SEGMENT',
  operator: condition.type,
  value: (condition.segments || []).map((segment) => segment.name),
});

export const fromFilter = (filter, typeExtractor) => {
  const conditions = [
    ..._.get(filter, 'and', []).flatMap((e) => e.conditions || []),
    ..._.get(filter, 'or', []).flatMap((e) => e.conditions || []),
  ].map((condition) => fromCondition(condition, typeExtractor));

  const segmentConditions = [
    ..._.get(filter, 'and', []).flatMap((e) => e.segmentConditions || []),
    ..._.get(filter, 'or', []).flatMap((e) => e.segmentConditions || []),
  ].map((condition) => fromSegmetCondition(condition));

  return {
    clauses: [...conditions, ...segmentConditions],
    concatOperator: _.get(filter, 'or.length') ? '||' : '&&',
  };
};

export const toCondition = ({ field, operator, value }) =>
  !nullOperators.includes(operator)
    ? {
        field,
        operator,
        value,
      }
    : toNullCondition({ field, operator });

export const toSegmentCondition = (clause) => ({
  type: clause.operator,
  segments: (clause.value || []).map((e) => ({ name: e })),
});

export const toFilter = (filter) => ({
  from: filter.from,
  to: filter.to,
  [_.get(filter, 'concatOperator', '&&') === '||' ? 'or' : 'and']: [
    {
      conditions: _.get(filter, 'clauses', [])
        .filter((c) => !segmentFilter(c))
        .filter(emptyConditionFilter)
        .map(toCondition),
      segmentConditions: _.get(filter, 'clauses', [])
        .filter(segmentFilter)
        .filter(emptyConditionFilter)
        .map(toSegmentCondition),
    },
  ],
});

export const toDashboardGlobalFilter = (filter) => ({
  from: filter.from,
  to: filter.to,
});

export const invertOperator = (operator) => {
  switch (operator) {
    case operators.ALL:
      return operators.NONE;
    case operators.ANY:
      return operators.NONE;
    case operators.NONE:
      return operators.ANY;
    case operators.in:
      return operators.not_in;
    case operators.is_null:
      return operators.not_null;
    case operators.not_in:
      return operators.in;
    case operators.not_null:
      return operators.is_null;
    case operators.like_case_insensitive:
      return operators.not_like_case_insensitive;
    case operators.not_like_case_insensitive:
      return operators.like_case_insensitive;
    case operators.regexp:
      return operators.not_regexp;
    case operators.not_regexp:
      return operators.regexp;
    case operators['<']:
      return operators['>='];
    case operators['<=']:
      return operators['>'];
    case operators['>']:
      return operators['<='];
    case operators['>=']:
      return operators['<'];
    case operators['=']:
      return operators['!='];
    case operators['!=']:
      return operators['='];
    default:
      return operators.is_null;
  }
};

const invert = (clause) => ({
  ...clause,
  operator: invertOperator(clause.operator),
});

const invertWithBetween = (clause) => {
  if (clause.operator === operators.between) {
    return [
      { ...clause, value: [0, clause.value[0]] },
      { ...clause, value: [clause.value[1], 4000000000000] },
    ]
      .map(toCondition)
      .map((condition) => ({ conditions: [condition] }));
  }
  return [{ conditions: [invert(clause)].map(toCondition) }];
};

export const toOthersFilter = (filter, invertId) => {
  const conditions = _.get(filter, 'clauses', [])
    .filter((c) => !segmentFilter(c))
    .filter(emptyConditionFilter)
    .filter((clause) => clause.id !== invertId)
    .map(toCondition);

  const othersCondition = _.get(filter, 'clauses', [])
    .filter((c) => !segmentFilter(c))
    .filter(emptyConditionFilter)
    .find((clause) => clause.id === invertId);

  const othersFilterGroupHolder = !othersCondition
    ? {}
    : {
        or: invertWithBetween(othersCondition),
      };

  const segmentConditions = _.get(filter, 'clauses', [])
    .filter(segmentFilter)
    .filter(emptyConditionFilter)
    .map((clause) => (clause.id === invertId ? invert(clause) : clause))
    .map(toSegmentCondition);

  const filterGroups = [
    {
      conditions,
      segmentConditions,
      ...othersFilterGroupHolder,
    },
  ];

  const filterGroupsType = _.get(filter, 'concatOperator', '&&') === '||' ? 'or' : 'and';

  return {
    from: filter.from,
    to: filter.to,
    [filterGroupsType]: filterGroups,
  };
};
